import { getAuthenticatedClient } from "../supabaseClient";

/**
 * @function addChargerCost - Charger Cost added, called when initializing new user
 * @param {*} userId - userId to be updated
 * @returns api response
 */
export const addChargerCost = async (userId) => {
  if (!userId) {
    throw new Error("User ID required");
  }

  const supabase = getAuthenticatedClient();

  const { data, error } = await supabase.from("charger costs").insert({
    id: userId,
    cost_less_than_10_kw: 16428,
    cost_10_20_kw: 20947,
    cost_25_kw: 40229,
    cost_180_200_kw: 93650,
    install_less_than_10_kw: 21400,
    install_10_20_kw: 21400,
    install_25_kw: 26400,
    install_180_200_kw: 28200,
    contingency: 15,
  });

  if (error) {
    return { error: error.message };
  }

  return { message: "Charger cost added successfully", data };
};

/**
 * @function fetchChargerData - Fetches charger cost data from the "charger costs" table
 * @param {*} userId - userId to fetch data for
 * @returns api response with charger cost data or null if not found
 */
export const fetchChargerData = async (userId) => {
  const supabase = getAuthenticatedClient();

  try {
    const { data, error } = await supabase
      .from("charger costs")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      return { error: error.message, data: null };
    }

    return { data };
  } catch (error) {
    console.error("Error fetching charger data:", error);
    return { error: error.message, data: null };
  }
};

/**
 * @function fetchDefaultChargerData - Fetches charger cost data from the "default data" table's charger_costs column
 * @param {*} userId - userId to fetch data for
 * @returns api response with charger cost data or null if not found
 */
export const fetchDefaultChargerData = async (userId) => {
  const supabase = getAuthenticatedClient();

  try {
    const { data, error } = await supabase
      .from("default data")
      .select("charger_costs")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      return { error: error.message, data: null };
    }

    if (!data || !data.charger_costs) {
      return { data: null };
    }

    // Extract charger_costs from the JSONB column
    // charger_costs is stored as an array, so we need to handle both array and object formats
    const chargerCostsData = Array.isArray(data.charger_costs)
      ? data.charger_costs[0]
      : data.charger_costs;

    return { data: chargerCostsData };
  } catch (error) {
    console.error("Error fetching default charger data:", error);
    return { error: error.message, data: null };
  }
};

/**
 * @function updateChargerData - Updates or creates charger cost data in the "charger costs" table for clients
 * @param {*} userId - userId to be updated
 * @param {*} updates - Object containing charger cost fields to update
 * @returns api response
 */
export const updateChargerData = async (userId, updates) => {
  const supabase = getAuthenticatedClient();

  try {
    // Use upsert to insert if record doesn't exist, or update if it does
    const { data, error } = await supabase
      .from("charger costs")
      .upsert([{ id: userId, ...updates }])
      .select("*");

    if (error) {
      return { error: error.message };
    }

    if (!data || data.length === 0) {
      return { error: "Failed to save charger data" };
    }

    return { data };
  } catch (error) {
    console.error("Error updating charger data:", error);
    throw error;
  }
};

export const updateDefaultChargerData = async (userId, updates) => {
  const supabase = getAuthenticatedClient();

  try {
    // First, fetch the current charger_costs from default data
    const { data: defaultData, error: fetchError } = await supabase
      .from("default data")
      .select("charger_costs")
      .eq("id", userId)
      .maybeSingle();

    if (fetchError) {
      return { error: fetchError.message };
    }

    if (!defaultData) {
      return { error: "Default data not found for user" };
    }

    //Update the charger_costs column in default data table
    const { data, error } = await supabase
      .from("default data")
      .update({ charger_costs: [updates] })
      .eq("id", userId)
      .select("charger_costs");

    if (error) {
      return { error: error.message };
    }

    if (!data || data.length === 0) {
      return { error: "Charger data not found" };
    }

    //Return in the same format as before (with id field)
    return { data: [{ ...data[0].charger_costs, id: userId }] };
  } catch (error) {
    console.error("Error updating charger data:", error);
    throw error;
  }
};
